import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate } from "@/lib/api";
import { parseList, fmtAgeMonths } from "@/lib/sp";

const opts = (pairs) => pairs.map(([value, label]) => ({ value, label }));
const bold = (v) => <span className="font-medium text-stone-900">{v || "—"}</span>;
const active = (i) => <StatusBadge value={i.is_active === false ? "suspendu" : "actif"} />;
const patientRel = { type: "relation", resource: "sp/patients", labelKey: "full_name" };
const doctorRel = { type: "relation", resource: "sp/users", labelKey: "full_name", where: (u) => u.role === "DOCTOR" };
const R = "write:records";

export const usersConfig = {
  resource: "sp/users", testKey: "users", title: "Utilisateurs", singular: "Utilisateur",
  subtitle: "Comptes de l'application Sehatic-Pedia : parents, médecins et administrateurs.",
  writePerm: "write:users", defaults: { role: "PARENT", is_active: true, is_verified: true },
  searchKeys: ["full_name", "email", "phone_number", "role"],
  columns: [
    { key: "full_name", label: "Nom", render: (i) => bold(i.full_name) },
    { key: "email", label: "E-mail" },
    { key: "role", label: "Rôle", render: (i) => <StatusBadge value={i.role} /> },
    { key: "phone_number", label: "Téléphone" },
    { key: "children_count", label: "Enfants", render: (i) => (i.role === "PARENT" ? i.children_count : "—") },
    { key: "created_at", label: "Inscrit le", render: (i) => fmtDate(i.created_at?.slice(0, 10)) },
    { key: "is_active", label: "Statut", render: active },
  ],
  fields: [
    { key: "email", label: "E-mail (identifiant)", type: "email", required: true },
    { key: "role", label: "Rôle", type: "select", required: true, createOnly: true, options: opts([["PARENT", "Parent"], ["DOCTOR", "Médecin"], ["LAB", "Laboratoire"], ["ADMIN", "Administrateur"]]) },
    { key: "first_name", label: "Prénom", required: true },
    { key: "last_name", label: "Nom", required: true },
    { key: "phone_number", label: "Téléphone" },
    { key: "is_active", label: "Compte actif", type: "bool" },
    { key: "is_verified", label: "Compte vérifié", type: "bool" },
  ],
  rowLink: (i) => `/utilisateurs/${i.id}`,
};

export const doctorsConfig = {
  resource: "sp/doctor_profiles", testKey: "doctors", title: "Médecins", singular: "Médecin",
  subtitle: "Profils des praticiens inscrits dans l'application et validation de leur affiliation.",
  writePerm: "write:doctors", noCreate: true,
  rowLink: (i) => `/utilisateurs/${i.user_id}`,
  searchKeys: ["full_name", "specialty", "category", "license_number", "wilaya_name", "work_address"],
  columns: [
    { key: "full_name", label: "Nom", render: (i) => bold(i.full_name) },
    { key: "category", label: "Catégorie" },
    { key: "specialty", label: "Spécialité" },
    { key: "license_number", label: "N° d'inscription" },
    { key: "wilaya_name", label: "Wilaya", render: (i) => i.wilaya_name || "—" },
    { key: "work_address", label: "Adresse du cabinet" },
    { key: "affiliation_status", label: "Affiliation", render: (i) => <StatusBadge value={i.affiliation_status || "PENDING"} /> },
  ],
  fields: [
    { key: "affiliation_status", label: "Affiliation", type: "select", options: opts([["PENDING", "En attente"], ["APPROVED", "Validé"], ["REJECTED", "Refusé"]]) },
    { key: "specialty", label: "Spécialité" },
    { key: "license_number", label: "N° d'inscription" },
    { key: "work_address", label: "Adresse du cabinet", full: true },
  ],
};

export const patientsConfig = {
  resource: "sp/patients", testKey: "patients", title: "Enfants", singular: "Enfant",
  subtitle: "Dossiers des enfants suivis dans l'application. Ajout, modification et suppression envoyés à l'application.",
  writePerm: R, defaults: { gender: "F" },
  rowLink: (i) => `/enfants/${i.id}`,
  searchKeys: ["full_name", "parent_names", "wilaya_name", "blood_type"],
  columns: [
    { key: "full_name", label: "Enfant", render: (i) => bold(i.full_name) },
    { key: "age_months", label: "Âge", render: (i) => fmtAgeMonths(i.age_months) },
    { key: "gender", label: "Sexe", render: (i) => (i.gender === "F" ? "Fille" : i.gender === "M" ? "Garçon" : "—") },
    { key: "blood_type", label: "Groupe" },
    { key: "parent_names", label: "Parent(s)" },
    { key: "wilaya_name", label: "Wilaya", render: (i) => i.wilaya_name || "—" },
    { key: "allergies", label: "Allergies", render: (i) => parseList(i.allergies).join(", ") || "—" },
  ],
  fields: [
    { key: "first_name", label: "Prénom", required: true },
    { key: "last_name", label: "Nom" },
    { key: "birth_date", label: "Date de naissance", type: "date", required: true },
    { key: "gender", label: "Sexe", type: "select", required: true, options: opts([["F", "Fille"], ["M", "Garçon"]]) },
    { key: "blood_type", label: "Groupe sanguin", type: "select", options: opts(["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((g) => [g, g])) },
    { key: "wilaya", label: "Code wilaya" },
    { key: "birth_weight_g", label: "Poids de naissance (g)", type: "number" },
    { key: "birth_height_cm", label: "Taille de naissance (cm)", type: "number" },
    { key: "allergies", label: "Allergies", type: "list", full: true },
    { key: "chronic_conditions", label: "Maladies chroniques", type: "list", full: true },
    { key: "parent_ids", label: "Parent(s)", type: "relations", resource: "sp/users", labelKey: "full_name", where: (u) => u.role === "PARENT", required: true, full: true },
    { key: "notes", label: "Notes", type: "textarea", full: true },
  ],
};

export const appointmentsConfig = {
  resource: "sp/appointments", testKey: "appointments", title: "Rendez-vous", singular: "Rendez-vous",
  subtitle: "Rendez-vous pris dans l'application. Le changement de statut est envoyé à l'application.",
  writePerm: "write:appointments", defaults: { status: "CONFIRMED" },
  searchKeys: ["patient_name", "doctor_name", "reason", "status"],
  columns: [
    { key: "start_time", label: "Date", render: (i) => bold(fmtDate(i.start_time?.slice(0, 16))) },
    { key: "patient_name", label: "Enfant" },
    { key: "doctor_name", label: "Médecin" },
    { key: "reason", label: "Motif" },
    { key: "status", label: "Statut", render: (i) => <StatusBadge value={i.status} /> },
  ],
  fields: [
    { key: "patient_id", label: "Enfant", ...patientRel, required: true },
    { key: "doctor_id", label: "Médecin", ...doctorRel, required: true },
    { key: "start_time", label: "Début", type: "datetime-local", required: true },
    { key: "end_time", label: "Fin", type: "datetime-local" },
    { key: "status", label: "Statut", type: "select", options: opts([["REQUESTED", "Demandé"], ["CONFIRMED", "Confirmé"], ["COMPLETED", "Terminé"], ["CANCELLED", "Annulé"]]) },
    { key: "reason", label: "Motif", full: true },
  ],
};

const meds = (v) => parseList(v).map((m) => m.name).filter(Boolean).join(", ") || "—";

export const consultationsConfig = {
  resource: "sp/consultations", testKey: "consultations", embedded: true, title: "Consultations", singular: "Consultation", writePerm: R,
  searchKeys: ["patient_name", "doctor_name", "diagnosis"],
  columns: [
    { key: "consultation_date", label: "Date", render: (i) => bold(fmtDate(i.consultation_date?.slice(0, 16))) },
    { key: "patient_name", label: "Enfant" },
    { key: "doctor_name", label: "Médecin" },
    { key: "diagnosis", label: "Diagnostic" },
    { key: "medications", label: "Traitement", render: (i) => meds(i.medications) },
  ],
  fields: [
    { key: "patient_id", label: "Enfant", ...patientRel, required: true },
    { key: "doctor_id", label: "Médecin", ...doctorRel, required: true },
    { key: "consultation_date", label: "Date", type: "datetime-local", required: true },
    { key: "weight_used_kg", label: "Poids (kg)", type: "number" },
    { key: "diagnosis", label: "Diagnostic", full: true },
    { key: "medications", label: "Médicaments", type: "medlist", full: true },
    { key: "clinical_notes", label: "Notes cliniques", type: "textarea", full: true },
  ],
};

export const prescriptionsConfig = {
  resource: "sp/prescriptions", testKey: "prescriptions", embedded: true, title: "Ordonnances", singular: "Ordonnance", writePerm: R,
  searchKeys: ["patient_name", "doctor_name"],
  columns: [
    { key: "issued_at", label: "Émise le", render: (i) => bold(fmtDate(i.issued_at?.slice(0, 16))) },
    { key: "patient_name", label: "Enfant" },
    { key: "doctor_name", label: "Médecin" },
    { key: "medications_payload", label: "Médicaments", render: (i) => meds(i.medications_payload) },
  ],
  fields: [
    { key: "patient_id", label: "Enfant", ...patientRel, required: true },
    { key: "doctor_id", label: "Médecin", ...doctorRel, required: true },
    { key: "issued_at", label: "Émise le", type: "datetime-local" },
    { key: "medications_payload", label: "Médicaments", type: "medlist", full: true },
  ],
};

export const vaccinationsConfig = {
  resource: "sp/vaccinations", testKey: "vaccinations", embedded: true, title: "Vaccinations", singular: "Vaccination", writePerm: R, defaults: { status: "SCHEDULED", dose_number: 1 },
  searchKeys: ["patient_name", "vaccine_name", "status"],
  columns: [
    { key: "scheduled_date", label: "Prévu le", render: (i) => bold(fmtDate(i.scheduled_date)) },
    { key: "patient_name", label: "Enfant" },
    { key: "vaccine_name", label: "Vaccin" },
    { key: "dose_number", label: "Dose" },
    { key: "administered_date", label: "Fait le", render: (i) => fmtDate(i.administered_date) },
    { key: "status", label: "Statut", render: (i) => <StatusBadge value={i.status} /> },
  ],
  fields: [
    { key: "patient_id", label: "Enfant", ...patientRel, required: true },
    { key: "vaccine_name", label: "Vaccin", required: true },
    { key: "dose_number", label: "Dose n°", type: "number" },
    { key: "scheduled_date", label: "Prévu le", type: "date", required: true },
    { key: "administered_date", label: "Fait le", type: "date" },
    { key: "status", label: "Statut", type: "select", options: opts([["SCHEDULED", "Prévu"], ["ADMINISTERED", "Fait"], ["MISSED", "Manqué"], ["CANCELLED", "Annulé"]]) },
    { key: "batch_number", label: "N° de lot" },
    { key: "notes", label: "Notes", full: true },
  ],
};

const list = (v) => parseList(v).join(", ") || "—";

export const documentsConfig = {
  resource: "sp/med_documents", testKey: "documents", embedded: true, title: "Documents", writePerm: R, noCreate: true, noEdit: true,
  searchKeys: ["name", "patient_name", "owner_name", "content_type"],
  columns: [
    { key: "created_at", label: "Ajouté le", render: (i) => bold(fmtDate(i.created_at?.slice(0, 16))) },
    { key: "name", label: "Document" },
    { key: "patient_name", label: "Enfant" },
    { key: "owner_name", label: "Ajouté par" },
    { key: "content_type", label: "Type", render: (i) => (i.content_type || "").split("/").pop().toUpperCase() || "—" },
    { key: "size", label: "Taille", render: (i) => (i.size ? `${Math.max(1, Math.round(i.size / 1024))} Ko` : "—") },
  ],
  fields: [],
};

export const pathologiesConfig = {
  resource: "sp/pathologies", testKey: "pathologies", embedded: true, title: "Pathologies", singular: "Pathologie",
  writePerm: "write:referentials",
  searchKeys: ["name", "code", "category", "icd11_code"],
  defaults: { contagious: false },
  columns: [
    { key: "code", label: "Code" },
    { key: "name", label: "Pathologie", render: (i) => bold(i.name) },
    { key: "category", label: "Catégorie" },
    { key: "contagious", label: "Contagieuse", render: (i) => (i.contagious ? "Oui" : "Non") },
    { key: "epidemic_threshold", label: "Seuil épidémique", render: (i) => i.epidemic_threshold ?? "—" },
    { key: "first_line", label: "1re intention", render: (i) => list(i.first_line) },
  ],
  fields: [
    { key: "code", label: "Code CIM-10", required: true },
    { key: "name", label: "Nom", required: true },
    { key: "category", label: "Catégorie" },
    { key: "contagious", label: "Contagieuse", type: "bool" },
    { key: "epidemic_threshold", label: "Seuil épidémique (cas / 7 jours)", type: "number" },
    { key: "icd11_code", label: "Code CIM-11" },
    { key: "icd11_title", label: "Intitulé CIM-11", full: true },
    { key: "first_line", label: "Traitements de 1re intention", type: "list", full: true },
    { key: "alternatives", label: "Alternatives", type: "list", full: true },
    { key: "avoid", label: "À éviter", type: "list", full: true },
  ],
};

export const drugsConfig = {
  resource: "sp/drugs", testKey: "drugs", embedded: true, title: "Médicaments", singular: "Médicament",
  writePerm: "write:referentials",
  searchKeys: ["dci", "drug_class", "brands"],
  columns: [
    { key: "dci", label: "DCI", render: (i) => bold(i.dci) },
    { key: "drug_class", label: "Classe" },
    { key: "brands", label: "Marques", render: (i) => list(i.brands) },
    { key: "dose_mg_per_kg", label: "mg/kg", render: (i) => i.dose_mg_per_kg ?? i.dose_text ?? "—" },
    { key: "max_mg", label: "Max/prise (mg)", render: (i) => i.max_mg ?? "—" },
    { key: "min_age_months", label: "Âge min.", render: (i) => fmtAgeMonths(i.min_age_months) },
  ],
  fields: [
    { key: "dci", label: "DCI", required: true },
    { key: "drug_class", label: "Classe" },
    { key: "dose_mg_per_kg", label: "Dose (mg/kg)", type: "number" },
    { key: "max_mg", label: "Dose max par prise (mg)", type: "number" },
    { key: "max_mg_day", label: "Dose max par jour (mg)", type: "number" },
    { key: "frequency", label: "Fréquence" },
    { key: "duration_days", label: "Durée (jours)", type: "number" },
    { key: "min_age_months", label: "Âge minimum (mois)", type: "number" },
    { key: "dose_text", label: "Posologie (texte libre)", full: true },
    { key: "brands", label: "Marques", type: "list", full: true },
    { key: "forms", label: "Formes", type: "list", full: true },
    { key: "contraindications", label: "Contre-indications", type: "list", full: true },
    { key: "allergy_classes", label: "Classes allergiques", type: "list", full: true },
    { key: "interactions", label: "Interactions", type: "list", full: true },
  ],
};

export const facilitiesConfig = {
  resource: "sp/medical_facilities", testKey: "facilities", embedded: true, title: "Établissements", singular: "Établissement",
  writePerm: "write:referentials",
  searchKeys: ["name", "address", "email"],
  defaults: { is_active: true },
  columns: [
    { key: "name", label: "Nom", render: (i) => bold(i.name) },
    { key: "address", label: "Adresse" },
    { key: "phone", label: "Téléphone" },
    { key: "email", label: "E-mail" },
    { key: "is_active", label: "Statut", render: active },
  ],
  fields: [
    { key: "name", label: "Nom", required: true },
    { key: "phone", label: "Téléphone" },
    { key: "email", label: "E-mail", type: "email" },
    { key: "is_active", label: "Actif", type: "bool" },
    { key: "address", label: "Adresse", full: true },
  ],
};


const VALID = opts([["A_VALIDER", "À valider"], ["VALIDE", "Validé"], ["REJETE", "Rejeté"]]);
const d10 = (v) => fmtDate(v?.slice(0, 10));

export const growthConfig = {
  resource: "sp/growth_metrics", testKey: "growth", embedded: true, title: "Croissance", singular: "Mesure", writePerm: R,
  searchKeys: ["patient_name"],
  columns: [
    { key: "recorded_at", label: "Date", render: (i) => bold(d10(i.recorded_at)) },
    { key: "patient_name", label: "Enfant" },
    { key: "weight_kg", label: "Poids (kg)", render: (i) => i.weight_kg ?? "—" },
    { key: "height_cm", label: "Taille (cm)", render: (i) => i.height_cm ?? "—" },
    { key: "head_circ_cm", label: "PC (cm)", render: (i) => i.head_circ_cm ?? "—" },
    { key: "bmi", label: "IMC", render: (i) => i.bmi ?? "—" },
  ],
  fields: [
    { key: "patient_id", label: "Enfant", ...patientRel, required: true },
    { key: "recorded_at", label: "Date", type: "date", required: true },
    { key: "weight_kg", label: "Poids (kg)", type: "number" },
    { key: "height_cm", label: "Taille (cm)", type: "number" },
    { key: "head_circ_cm", label: "Périmètre crânien (cm)", type: "number" },
  ],
};

export const labsConfig = {
  resource: "sp/lab_results", testKey: "labs", embedded: true, title: "Analyses", singular: "Analyse", writePerm: R, defaults: { status: "A_VALIDER" },
  searchKeys: ["patient_name", "lab_name", "status"],
  columns: [
    { key: "date", label: "Date", render: (i) => bold(d10(i.date)) },
    { key: "patient_name", label: "Enfant" },
    { key: "lab_name", label: "Laboratoire" },
    { key: "results", label: "Résultats", render: (i) => parseList(i.results).map((r) => `${r.code} ${r.value ?? ""}`).join(", ") || "—" },
    { key: "status", label: "Statut", render: (i) => <StatusBadge value={i.status} /> },
  ],
  fields: [
    { key: "patient_id", label: "Enfant", ...patientRel, required: true },
    { key: "date", label: "Date", type: "date", required: true },
    { key: "lab_name", label: "Laboratoire" },
    { key: "status", label: "Statut", type: "select", options: VALID },
    { key: "notes", label: "Notes / résultats", type: "textarea", full: true },
  ],
};

export const imagingConfig = {
  resource: "sp/imaging_exams", testKey: "imaging", embedded: true, title: "Imagerie", singular: "Examen", writePerm: R, defaults: { status: "A_VALIDER" },
  searchKeys: ["patient_name", "exam_code", "facility", "conclusion"],
  columns: [
    { key: "date", label: "Date", render: (i) => bold(d10(i.date)) },
    { key: "patient_name", label: "Enfant" },
    { key: "exam_code", label: "Examen" },
    { key: "conclusion", label: "Conclusion" },
    { key: "status", label: "Statut", render: (i) => <StatusBadge value={i.status} /> },
  ],
  fields: [
    { key: "patient_id", label: "Enfant", ...patientRel, required: true },
    { key: "exam_code", label: "Code examen (ex. RX_THORAX)", required: true },
    { key: "date", label: "Date", type: "date", required: true },
    { key: "facility", label: "Établissement" },
    { key: "status", label: "Statut", type: "select", options: VALID },
    { key: "indication", label: "Indication", full: true },
    { key: "findings", label: "Résultats", type: "textarea", full: true },
    { key: "conclusion", label: "Conclusion", full: true },
  ],
};

export const grantsConfig = {
  resource: "sp/access_grants", testKey: "grants", embedded: true, title: "Partages d'accès", singular: "Partage", writePerm: R, defaults: { access_level: "FULL_ACCESS" },
  searchKeys: ["patient_name", "doctor_name"],
  columns: [
    { key: "created_at", label: "Créé le", render: (i) => bold(d10(i.created_at)) },
    { key: "patient_name", label: "Enfant" },
    { key: "doctor_name", label: "Médecin" },
    { key: "access_level", label: "Accès", render: (i) => (i.access_level === "READ_ONLY" ? "Lecture seule" : "Complet") },
    { key: "expires_at", label: "Expire le", render: (i) => d10(i.expires_at) },
  ],
  fields: [
    { key: "patient_id", label: "Enfant", ...patientRel, required: true },
    { key: "doctor_id", label: "Médecin", ...doctorRel, required: true },
    { key: "access_level", label: "Accès", type: "select", required: true, options: opts([["FULL_ACCESS", "Complet"], ["READ_ONLY", "Lecture seule"]]) },
    { key: "expires_at", label: "Expire le", type: "datetime-local" },
  ],
};

export const symptomsConfig = {
  resource: "sp/symptom_submissions", testKey: "symptoms", embedded: true, title: "Signalements", writePerm: R, noCreate: true, noEdit: true,
  searchKeys: ["patient_name", "ai_summary"],
  columns: [
    { key: "submitted_at", label: "Date", render: (i) => bold(fmtDate((i.submitted_at || i.created_at)?.slice(0, 16))) },
    { key: "patient_name", label: "Enfant" },
    { key: "red_flags", label: "Signes d'alerte", render: (i) => list(i.red_flags) },
  ],
  fields: [],
};

export const epiCasesConfig = {
  resource: "sp/epi_cases", testKey: "epicases", embedded: true, title: "Cas épidémiques", singular: "Cas", writePerm: R, defaults: { count: 1 },
  searchKeys: ["pathology_name", "wilaya", "icd_code"],
  columns: [
    { key: "date", label: "Date", render: (i) => bold(d10(i.date)) },
    { key: "pathology_name", label: "Pathologie" },
    { key: "count", label: "Cas", render: (i) => i.count ?? 1 },
    { key: "wilaya", label: "Wilaya" },
    { key: "age_months", label: "Âge", render: (i) => fmtAgeMonths(i.age_months) },
    { key: "gender", label: "Sexe", render: (i) => (i.gender === "F" ? "Fille" : i.gender === "M" ? "Garçon" : "—") },
  ],
  fields: [
    { key: "date", label: "Date", type: "date", required: true },
    { key: "pathology_name", label: "Pathologie", type: "relation", resource: "sp/pathologies", labelKey: "name", valueKey: "name", required: true },
    { key: "count", label: "Nombre de cas", type: "number" },
    { key: "wilaya", label: "Code wilaya" },
    { key: "age_months", label: "Âge (mois)", type: "number" },
    { key: "gender", label: "Sexe", type: "select", options: opts([["F", "Fille"], ["M", "Garçon"]]) },
  ],
};
